import './OrgDatabaseGrid.css'

import { Navigate, Route, Routes } from 'react-router-dom'

import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { Loading } from '@makyoapp/makyo-ui'

import { DbClientProvider, useDbClientContext } from '@/context/__project__/DbClientContext'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useSingleProjectContext } from '@/context/SingleProjectContext'
import { useIsProjectDb } from '@/hooks/useIsProjectDb'
import { useOrgPerm } from '@/hooks/useOrgPerm'
import { useParamId } from '@/hooks/useParamId'
import { ORG_DATABASE_ROUTE, ORG_ROUTE, PROJECT_ROUTE, TBL_ID_PARAM } from '@/shared/constants'
import { PageNotFound } from '@/components/PageNotFound'
import { PageUnauthorized } from '@/components/PageUnauthorized'

import DbTableGrid from './v2/DbTableGrid'

/**
 * Handle database table routing
 */
function OrgDatabaseGridRoutes() {
  const { project } = useSingleProjectContext()
  const isProject = useIsProjectDb()
  const { currentOrg } = useCurrentOrgContext()
  const { loadingDbClient, tables, dbClient } = useDbClientContext()
  const dbID = dbClient?.dbID

  if (loadingDbClient || !currentOrg || !dbClient) {
    return <Loading />
  }

  if (!dbID) {
    return <PageNotFound />
  }

  const firstTableId = tables[0]?.id

  return (
    <div className='wrapper'>
      <div className='flex-1 overflow-auto h-full'>
        <Routes>
          <Route
            index
            element={
              <Navigate
                to={
                  isProject
                    ? `${PROJECT_ROUTE}/${project?.id}/${ORG_DATABASE_ROUTE}/${firstTableId}`
                    : `${ORG_ROUTE}/${currentOrg.id}/${ORG_DATABASE_ROUTE}/${dbID}/${firstTableId}`
                }
                replace={true}
              />
            }
          />
          <Route path={`${TBL_ID_PARAM}/*`} element={<DbTableGrid dbID={dbID} orgID={currentOrg.id} />} />
        </Routes>
      </div>
    </div>
  )
}

/**
 * Main Database Grid component,
 * it can imported directly in AppBuilder (project) page, but you need to wrap it in DbClientProvider
 */
export const OrgDatabaseGrid = () => {
  const canView = useOrgPerm(OrgPermission.DBCreateLinkDatabasesAndTables)
  return !canView ? <PageUnauthorized /> : <OrgDatabaseGridRoutes />
}

/**
 * Grid Wrapper for Database Routes (`/org/:orgId/db/:dbId/table/:tableId`)
 * org -> database -> table page
 */
export const OrgDatabaseGridPage = () => {
  const dbIdParam = useParamId('db') as string

  return (
    <DbClientProvider databaseID={dbIdParam}>
      <OrgDatabaseGrid />
    </DbClientProvider>
  )
}
